import React, { useState, useEffect } from 'react';
import api from '../api';

/* ── Single menu card with expand + bulk archive ─────────────────────── */
function MenuCard({ menu, onRefresh }) {
  const [expanded, setExpanded]     = useState(false);
  const [detail, setDetail]         = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [statusFilter, setStatusFilter]   = useState('active'); // 'active' | 'archived' | 'all'
  const [selected, setSelected]     = useState(new Set());      // Set of item ids checked
  const [bulkBusy, setBulkBusy]     = useState(false);
  const [msg, setMsg]               = useState('');

  async function loadDetail(status = statusFilter) {
    setLoadingDetail(true);
    setSelected(new Set());
    try {
      const { data } = await api.get(`/menu/${menu.id}?status=${status}`);
      setDetail(data);
    } catch { /* fail silently */ }
    finally { setLoadingDetail(false); }
  }

  async function toggleExpand() {
    if (!expanded && !detail) await loadDetail();
    setExpanded(e => !e);
  }

  async function changeFilter(s) {
    setStatusFilter(s);
    if (expanded) await loadDetail(s);
  }

  function toggleItem(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function selectAll() {
    const allIds = (detail?.items || []).map(i => i.id);
    setSelected(new Set(allIds));
  }

  async function bulkUpdate(status) {
    if (!selected.size) return;
    setBulkBusy(true);
    try {
      await api.patch('/menu/items/bulk', { ids: [...selected], status });
      setMsg(`${selected.size} item${selected.size !== 1 ? 's' : ''} ${status === 'archived' ? 'archived' : 'restored'}`);
      setTimeout(() => setMsg(''), 3000);
      await loadDetail(statusFilter);
      onRefresh?.();
    } catch { setMsg('Action failed'); }
    finally { setBulkBusy(false); }
  }

  const menuItems = detail?.items || [];

  const byCategory = menuItems.reduce((acc, item) => {
    const cat = item.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <div className="card mb-3">
      {/* Header */}
      <div
        className="card-body"
        style={{ padding: '14px 18px', cursor: 'pointer', userSelect: 'none' }}
        onClick={toggleExpand}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span className="font-semibold" style={{ fontSize: 15 }}>{menu.name}</span>
            <span className="text-gray text-sm" style={{ marginLeft: 10 }}>{menu.item_count} dishes</span>
          </div>
          <span style={{ color: '#64748b', fontSize: 16 }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', padding: '14px 18px' }}>

          {/* Status filter + bulk action bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              {['active', 'archived', 'all'].map(s => (
                <button
                  key={s}
                  onClick={() => changeFilter(s)}
                  style={{
                    fontSize: 12, padding: '3px 10px', borderRadius: 4, cursor: 'pointer', border: 'none',
                    background: statusFilter === s ? 'rgba(13,148,136,0.2)' : 'rgba(100,116,139,0.1)',
                    color: statusFilter === s ? '#0d9488' : '#64748b', fontWeight: statusFilter === s ? 700 : 400,
                  }}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>

            {selected.size > 0 && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>{selected.size} selected</span>
                {statusFilter !== 'archived' && (
                  <button
                    onClick={() => bulkUpdate('archived')}
                    disabled={bulkBusy}
                    style={{ fontSize: 12, padding: '3px 10px', borderRadius: 4, cursor: 'pointer', border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.08)', color: '#ef4444' }}
                  >
                    {bulkBusy ? '…' : '📦 Archive'}
                  </button>
                )}
                {statusFilter === 'archived' && (
                  <button
                    onClick={() => bulkUpdate('active')}
                    disabled={bulkBusy}
                    style={{ fontSize: 12, padding: '3px 10px', borderRadius: 4, cursor: 'pointer', border: '1px solid rgba(13,148,136,0.4)', background: 'rgba(13,148,136,0.08)', color: '#0d9488' }}
                  >
                    {bulkBusy ? '…' : '✓ Restore'}
                  </button>
                )}
                <button onClick={() => setSelected(new Set())} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, cursor: 'pointer', border: 'none', background: 'rgba(100,116,139,0.1)', color: '#64748b' }}>✕</button>
              </div>
            )}

            {selected.size === 0 && menuItems.length > 0 && (
              <button onClick={selectAll} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 4, cursor: 'pointer', border: '1px solid rgba(100,116,139,0.2)', background: 'transparent', color: '#64748b' }}>
                Select all
              </button>
            )}
          </div>

          {msg && <div style={{ fontSize: 12, color: '#0d9488', marginBottom: 10, fontWeight: 600 }}>✓ {msg}</div>}

          {loadingDetail ? (
            <div className="loading" style={{ fontSize: 13 }}>Loading...</div>
          ) : (
            Object.entries(byCategory).map(([category, catItems]) => (
              <div key={category} style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                  {category}
                </div>
                {catItems.map(item => (
                  <div
                    key={item.id}
                    onClick={() => toggleItem(item.id)}
                    style={{
                      marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.05)',
                      cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 10,
                      opacity: item.status === 'archived' ? 0.5 : 1,
                    }}
                  >
                    {/* Checkbox */}
                    <div style={{
                      width: 16, height: 16, borderRadius: 3, flexShrink: 0, marginTop: 2,
                      border: `2px solid ${selected.has(item.id) ? '#0d9488' : 'rgba(100,116,139,0.4)'}`,
                      background: selected.has(item.id) ? '#0d9488' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {selected.has(item.id) && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 2 }}>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{item.name}</span>
                        {item.price != null && (
                          <span style={{ fontSize: 13, color: '#0d9488', fontWeight: 600 }}>${Number(item.price).toFixed(2)}</span>
                        )}
                        {item.status === 'archived' && (
                          <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: 'rgba(100,116,139,0.15)', color: '#64748b' }}>archived</span>
                        )}
                      </div>
                      {item.description && (
                        <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.5 }}>{item.description}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
          {!loadingDetail && menuItems.length === 0 && (
            <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 16 }}>
              {statusFilter === 'archived' ? 'No archived items.' : 'No active items.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Upload tab ──────────────────────────────────────────────────────── */
function UploadTab({ onMenuUploaded }) {
  const [status, setStatus] = useState('idle'); // idle | processing | done | error
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  async function handleFile(file) {
    if (!file) return;
    setStatus('processing');
    setError('');
    setResult(null);
    const fd = new FormData();
    fd.append('menu', file);
    try {
      const { data } = await api.post('/menu/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      setStatus('done');
      onMenuUploaded?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed — please try again.');
      setStatus('error');
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  }

  if (status === 'done' && result) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Menu Parsed</div>
          <div style={{ fontSize: 14, color: '#1e293b', marginBottom: 4 }}>
            <strong>{result.dish_count ?? result.item_count ?? '?'}</strong> dishes across{' '}
            <strong>{result.category_count ?? result.categories ?? '?'}</strong> categories
          </div>
          {result.menu_name && (
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Menu: {result.menu_name}</div>
          )}
          <button
            className="btn btn-primary btn-sm"
            onClick={() => { setStatus('idle'); setResult(null); }}
          >
            Upload Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">Upload Menu</div>
      <div className="card-body">
        {error && <div className="alert alert-error mb-3">{error}</div>}

        {(status === 'idle' || status === 'error') && (
          <>
            <label
              htmlFor="menu-upload-input"
              className={`upload-zone${dragOver ? ' drag-over' : ''}`}
              style={{ cursor: 'pointer', display: 'block' }}
              onDrop={onDrop}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
            >
              <div className="upload-icon">🍽️</div>
              <p className="upload-text">
                <strong>Drop or tap to upload your menu</strong><br />
                Image or PDF — Claude will read and parse it
              </p>
              <p className="text-xs text-gray mt-2">JPG, PNG, PDF — up to 20MB</p>
            </label>
            <input
              id="menu-upload-input"
              type="file"
              accept="image/*,application/pdf"
              style={{ position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none' }}
              onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); e.target.value = ''; }}
            />
          </>
        )}

        {status === 'processing' && (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🔍</div>
            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 12 }}>Claude is reading your menu...</div>
            <div className="progress-bar">
              <div className="progress-fill good" style={{ width: '70%', transition: 'width 2s' }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Menus list tab ──────────────────────────────────────────────────── */
function MenusListTab({ refreshSignal }) {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get('/menu')
      .then(({ data }) => setMenus(Array.isArray(data) ? data : (data.menus || [])))
      .catch(() => setError('Failed to load menus'))
      .finally(() => setLoading(false));
  }, [refreshSignal]);

  if (loading) return <div className="loading">Loading menus...</div>;
  if (error) return <div className="alert alert-error">{error}</div>;

  if (menus.length === 0) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
          No menus uploaded yet. Use the "Upload Menu" tab to scan one.
        </div>
      </div>
    );
  }

  function refresh() {
    setLoading(true);
    api.get('/menu')
      .then(({ data }) => setMenus(Array.isArray(data) ? data : (data.menus || [])))
      .catch(() => setError('Failed to load menus'))
      .finally(() => setLoading(false));
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, fontStyle: 'italic' }}>
        Expand a menu → select items → Archive to hide from active view, or Restore archived items.
      </div>
      {menus.map(menu => (
        <MenuCard key={menu.id} menu={menu} onRefresh={refresh} />
      ))}
    </div>
  );
}

// ── New Napua menu data parsed from Aug 2026 photos ──────────────────────
const NAPUA_SEED_DATA = [
  {
    name: 'Napua Lunch & All-Day Menu', menu_type: 'lunch',
    items: [
      { category: 'Appetizers', name: 'Calamari Katsu', price: 28, description: 'Furikake panko fried calamari steak, tomato poke, mango sambal aioli, unagi sauce' },
      { category: 'Appetizers', name: 'Olena Hummus', price: 20, description: 'Market produce, pickled onions, arugula, charcoal sourdough' },
      { category: 'Appetizers', name: 'Baked Crab & Artichoke Dip', price: 24, description: 'With spiced house-made corn chips' },
      { category: 'Appetizers', name: 'Portugese Bean Stew', price: 20, description: 'Arugula macadamia nut pesto, jasmine rice' },
      { category: 'Salads', name: 'Lemongrass Caesar', price: 24, description: 'Romaine lettuce, wonton chips, parmigiano crisps. Add: chicken $12, shrimp $14, fresh catch $14' },
      { category: 'Salads', name: "Ahupua'a", price: 26, description: 'Hirabara greens, Waimea tomato, seasonal fruits, goat cheese, honey garlic dressing, balsamic reduction' },
      { category: 'Salads', name: 'Greek', price: 26, description: 'Hirabara field greens, keiki cucumber, red onion, Waimea tomato, feta, yuzu herb vinaigrette' },
      { category: 'Sandwiches', name: 'Napua Burger', price: 25, description: '8oz chargrilled ground sirloin, local lettuce, tomato, onion, Up and Up sauce on brioche bun. Add: cheese $3, guacamole $5, smoked bacon $5' },
      { category: 'Sandwiches', name: 'Paniolo Burger', price: 29, description: '8oz chargrilled ground sirloin, beer-battered furikake onion rings, smoked bacon, cheddar cheese, Mr. Riggs guava BBQ sauce on brioche bun' },
      { category: 'Sandwiches', name: 'Grilled Market Catch', price: 28, description: '6oz chargrilled local fish, local lettuce, tomato, onion, tartar, lemon on brioche bun' },
      { category: 'Sandwiches', name: 'BBQ Kalua Pork Sandwich', price: 23, description: 'Kalua pork, Mr. Riggs Guava BBQ sauce, green goddess slaw on brioche bun' },
      { category: 'Sandwiches', name: 'Classic Tuna Salad', price: 21, description: 'Albacore tuna salad, Swiss cheese, local lettuce, tomato, onion on toasted sourdough' },
      { category: 'Sandwiches', name: 'Island Fish Tacos', price: 30, description: 'Seared market catch, tomato poke, guacamole, mango sambal aioli, Napa cabbage on flour tortillas' },
      { category: 'Sandwiches', name: 'Chickpea Falafel Tacos', price: 28, description: 'With olena hummus, cabbage, tomato poke, mango sambal aioli on flour tortilla' },
      { category: 'Bowls', name: 'Ahi Poke Bowl', price: 30, description: 'Scallions, ogo, inamona, sesame oil, Hawaiian sea salt. Served with jasmine rice, Kohala kimchee, teagu & ocean salad' },
      { category: 'Bowls', name: 'Kalua Pork & Cabbage Bowl', price: 26, description: 'Slow cooked lightly smoked pork shoulder, Hawaiian sea salt. Served with jasmine rice, Kohala kimchee, teagu & ocean salad' },
      { category: 'Bowls', name: 'Fresh Market Catch Bowl', price: 32, description: '6oz chargrilled local fish, herbed oil & lemon. Served with jasmine rice, Kohala kimchee, teagu & ocean salad' },
      { category: 'Keiki', name: 'Keiki Hamburger & Fries', price: 18, description: 'Add: cheese $3, bacon $4' },
      { category: 'Keiki', name: 'Keiki Grilled Local Catch & Jasmine Rice', price: 17, description: '3oz grilled local catch' },
      { category: 'Keiki', name: 'Keiki Grilled Cheese & Fries', price: 17, description: null },
      { category: 'Keiki', name: 'Keiki Eisenberg Hotdog & Fries', price: 17, description: null },
      { category: 'Keiki', name: 'Keiki Chicken Tenders & Fries', price: 17, description: '+$2 additional tender' },
      { category: 'Sides', name: 'French Fries', price: 9, description: null },
      { category: 'Sides', name: 'Blistered Green Beans', price: 11, description: 'Lemon & sea salt' },
      { category: 'Sides', name: 'Simple Salad', price: 11, description: 'Greens, tomato, cucumber' },
      { category: 'Sides', name: 'Furikake Beer Battered Onion Rings', price: 11, description: null },
    ],
  },
  {
    name: 'Napua Dinner Menu', menu_type: 'dinner',
    items: [
      { category: 'Starters', name: 'Calamari Katsu', price: 28, description: 'Panko furikake calamari steak, tomato poke, mango sambal aioli, unagi sauce' },
      { category: 'Starters', name: 'Ahi Crudo', price: 33, description: 'Cut daily with togarashi, cucumber kimchi, radish sprout, and bubu arare' },
      { category: 'Starters', name: 'Pupu BBQ Babyback Ribs', price: 32, description: 'Blistered garlic shishito peppers, guava BBQ sauce' },
      { category: 'Starters', name: 'Golden Beet Poke Tostada', price: 26, description: 'Pickled golden beets, lomi tomato, avocado, salsa negra' },
      { category: 'Starters', name: 'French Onion Bone Marrow', price: 35, description: 'Garlic rubbed sourdough, black bean caramelized onion, Hirabara Farm lettuce, lemon, capers' },
      { category: 'Starters', name: 'Crispy Tako', price: 29, description: 'Mojo rojo, pickled onion, grilled lemon' },
      { category: 'Salads', name: 'Crispy Broccolini Caesar', price: 24, description: 'Hirabara Farm little gems, fried broccolini, crispy potato, watermelon radish, 6-minute egg, vegan Caesar dressing, parmigiano reggiano' },
      { category: 'Salads', name: "Ahupua'a", price: 22, description: 'Hirabara Farm mixed greens, strawberries, Kamuela tomato, Big Island goat cheese, honey-garlic dressing' },
      { category: 'Mains', name: 'Chargrilled Black Pepper Ribeye Bistek', price: 70, description: 'Roasted onion, patis chimichurri, garlic, bay leaf, calamansi, shoyu with garlic rice' },
      { category: 'Mains', name: 'Fresh Catch', price: 62, description: 'Lop chong & garlic-shiitake rice, ginger bok choy, ogo namasu, oyster sauce' },
      { category: 'Mains', name: 'Kabocha Pumpkin Katsu Curry', price: 48, description: "Lemongrass coconut curry, roasted vegetables, rice, 'olena pickled mango" },
      { category: 'Mains', name: 'Grilled Rosemary Lamb Rack', price: 67, description: 'Portuguese style white bean stew, smoked sausage, macadamia nut pesto' },
      { category: 'Mains', name: 'Togarashi Blackened Ahi', price: 65, description: 'Chilled soba noodle salad, cabbage, cucumber, edamame, kamaboko, cilantro, pickled daikon, furikake, sesame honey miso dressing' },
      { category: 'Sides', name: 'Crispy Yukon Gold Potatoes', price: 12, description: 'Smoked ranch, bacon, scallion' },
      { category: 'Sides', name: 'Garlic Fried Rice', price: 12, description: 'Lap chong, shiitake' },
      { category: 'Sides', name: 'Ginger Bok Choy', price: 12, description: 'Roasted onion, bubu arare' },
      { category: 'Sides', name: 'Blistered Shishito Peppers', price: 12, description: 'Lime, garlic, paprika' },
    ],
  },
  {
    name: 'Napua Drinks Menu', menu_type: 'drinks',
    items: [
      { category: 'Cocktails', name: 'Classic Mai Tai', price: 18, description: 'Of course we have one..' },
      { category: 'Cocktails', name: 'Fresh Lime Squeeze', price: 18, description: 'We put da lime in da coconut, add vodka, and blend it all up' },
      { category: 'Cocktails', name: 'Lilikoi Margarita', price: 18, description: 'Served on the rocks, li hing mui rim' },
      { category: 'Cocktails', name: 'Pina Colada', price: 18, description: 'Rum, coconut cream and pineapple blended all up' },
      { category: 'Cocktails', name: 'Permanent Holiday', price: 18, description: 'Rum Haven organic coconut rum, muddled mint, lilikoi, soda' },
      { category: 'Cocktails', name: 'Lychee Spritz', price: 18, description: 'Giffard Lichi-Li, De Chanceny Brut Rose, lemon, soda' },
      { category: 'Cocktails', name: 'Pineapple Yuzu Sour', price: 18, description: 'Basil Hayden, yuzu, pineapple, mint simple syrup' },
      { category: 'Beer – Draft', name: 'Kona Brewing Co. Longboard Lager 4.6%', price: 9, description: null },
      { category: 'Beer – Draft', name: 'Kona Brewing Co. Fire Rock Pale Ale 5.5%', price: 9, description: null },
      { category: 'Beer – Draft', name: 'Maui Brewing Co. Big Swell IPA 6.8%', price: 9, description: null },
      { category: 'Beer – Draft', name: 'Big Island Brewhaus Golden Sabbath Belgian Style Abbey Ale 8.5%', price: 14, description: null },
      { category: 'Beer – Draft', name: 'Kona Brewing Co. Big Wave Golden Ale 4.4%', price: 9, description: null },
      { category: 'Beer – Draft', name: 'Kona Brewing Co. Kua Bay IPA 7.3%', price: 9, description: null },
      { category: 'Beer – Draft', name: 'Maui Brewing Co. Bikini Blonde Lager 4.8%', price: 9, description: null },
      { category: 'Beer – Draft', name: 'Ola Brew Da Hazy Light Session IPA 4.2%', price: 9, description: null },
      { category: 'Beer – Bottle/Can', name: 'Bud Light', price: 8, description: null },
      { category: 'Beer – Bottle/Can', name: 'Coors Light', price: 8, description: null },
      { category: 'Beer – Bottle/Can', name: 'Heineken', price: 8, description: null },
      { category: 'Beer – Bottle/Can', name: 'Corona', price: 8, description: null },
      { category: 'Beer – Bottle/Can', name: 'Seasonal Non-Alcoholic', price: 8, description: null },
      { category: 'Wine – Sparkling', name: 'Adami Bosco Di Gica Prosecco, Valdobbiadene DOCG Italia', price: 16, description: null },
      { category: 'Wine – Sparkling', name: 'De Chanceny Crémant de Loire Rosé, France', price: 16, description: null },
      { category: 'Wine – White', name: 'Lubanzi Chenin Blanc, Swartland South Africa 2025', price: 16, description: null },
      { category: 'Wine – White', name: 'Alois Lageder Terra Alpina Pinot Grigio, Trentino-Alto Adige Italy', price: 14, description: null },
      { category: 'Wine – White', name: 'Ameztoi Getariako Txakolina, Basque Country Spain', price: 19, description: null },
      { category: 'Wine – White', name: 'Echo Bay Sauvignon Blanc, Marlborough New Zealand', price: 16, description: null },
      { category: 'Wine – White', name: 'Jean-Jacques Auchere Sancerre, Loire France', price: 24, description: null },
      { category: 'Wine – White', name: 'Bravium Chardonnay, Russian River Valley CA', price: 19, description: null },
      { category: 'Wine – White', name: 'Nickle & Nickle "Truchard Vineyard" Chardonnay, Carneros CA', price: 24, description: null },
      { category: 'Wine – Rosé', name: 'Bieler Bandol Reserve Rosé, Bandol, Provence France 2023', price: 18, description: null },
      { category: 'Wine – Red', name: 'Failla Napua Cuvée Pinot Noir, Sonoma Coast California', price: 21, description: null },
      { category: 'Wine – Red', name: 'Burgess Contadina Cabernet Sauvignon, Napa CA 2018', price: 25, description: null },
      { category: 'Wine – Red', name: 'Chappellet Mountain Cuvée Proprietors Blend, Napa', price: 21, description: null },
      { category: 'Wine – Red', name: 'Turley Juvenile Zinfandel, California', price: 21, description: null },
      { category: 'Wine – Red', name: 'Famille Brunier Pigoulet, Vaucluse, Rhone France 2022', price: 17, description: null },
      { category: 'Desserts', name: 'Brownie', price: 15, description: 'Served warm with Tahitian vanilla ice cream and a chocolate drizzle' },
      { category: 'Desserts', name: 'Pineapple-Strawberry Cobbler', price: 14, description: 'Tahitian vanilla ice cream' },
      { category: 'Desserts', name: 'Seasonal Creme Brulee', price: 16, description: null },
      { category: 'Island Oasis Smoothies', name: 'Island Oasis Smoothie', price: 12, description: 'Choice of flavor: strawberry, mango, guava, vanilla, chocolate, lilikoi, banana, pina colada. +$1 each additional flavor' },
    ],
  },
];

/* ── Seed button (one-time use, hidden after success) ─────────────────── */
function SeedFromPhotosButton({ onDone }) {
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [msg, setMsg] = useState('');

  async function run() {
    setState('loading');
    try {
      const { data } = await api.post('/menu/structured-import', { menus: NAPUA_SEED_DATA });
      const total = data.results.reduce((s, r) => s + r.inserted, 0);
      setMsg(`✅ Imported ${total} items across ${data.results.length} menus`);
      setState('done');
      setTimeout(onDone, 1500);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Import failed');
      setState('error');
    }
  }

  if (state === 'done') return <div style={{ fontSize: 13, color: '#0d9488', fontWeight: 600 }}>{msg}</div>;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {state === 'error' && <span style={{ fontSize: 12, color: '#ef4444' }}>{msg}</span>}
      <button
        onClick={run}
        disabled={state === 'loading'}
        style={{
          fontSize: 12, padding: '4px 12px', borderRadius: 5, cursor: 'pointer',
          border: '1px solid rgba(13,148,136,0.4)', background: 'rgba(13,148,136,0.08)', color: '#0d9488',
        }}
      >
        {state === 'loading' ? '⏳ Importing…' : '📋 Import Aug 2026 menu from photos'}
      </button>
    </div>
  );
}

/* ── Main MenuManager component ──────────────────────────────────────── */
export default function MenuManager() {
  const [tab, setTab] = useState('menus');
  const [refreshSignal, setRefreshSignal] = useState(0);

  function handleMenuUploaded() {
    setRefreshSignal(n => n + 1);
    setTab('menus');
  }

  return (
    <>
      <div className="page-header">
        <span className="page-title">Menu Manager</span>
      </div>
      <div className="page-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <div className="flex gap-2">
            <button
              className={`btn btn-sm ${tab === 'menus' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setTab('menus')}
            >
              Menus
            </button>
            <button
              className={`btn btn-sm ${tab === 'upload' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setTab('upload')}
            >
              Upload Menu
            </button>
          </div>
          <SeedFromPhotosButton onDone={() => setRefreshSignal(n => n + 1)} />
        </div>

        {tab === 'upload' && <UploadTab onMenuUploaded={handleMenuUploaded} />}
        {tab === 'menus' && <MenusListTab refreshSignal={refreshSignal} />}
      </div>
    </>
  );
}
